import React, { useCallback, useMemo, useContext } from 'react';

import { useDataTransformPanelStore } from '@components/data-transform-panel/store';
import { eventDimensionNames, exploreLockedDimensionNames } from '@components/dimensions/typed';
import { updatePreset } from '@components/dimensions/utils/updatePresetUtil';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { useGetDimensionsList } from '@src/api/common/queryHooks';
import { ERenamePreset, mappedEventDimensionType } from '@src/common/constant/enum';

import { useDimensionPanelStore } from '../../store';
import { DimensionTableContainer } from '../DimensionTableContainer';
import DimensionContext from '@components/dimensions/DimensionContext';
import styles from './index.less';
import { useIsNewWorkflowPage } from '@hooks/useNewWorkflow';

export const DimensionTables: React.FC = () => {
  const isNewWorkflowPage = useIsNewWorkflowPage();

  const [
    size,
    preset,
    setPreset,
    curPresetName,
    lockedDimensionNames,
    eventAttributeDimensionNames,
  ] = useDimensionPanelStore((state) => [
    state.size,
    state.preset,
    state.setPreset,
    state.curPresetName,
    state.lockedDimensionNames,
    state.eventAttributeDimensionNames,
  ]);
  const transformPanelOpen = useDataTransformPanelStore((state) => state.transformPanelOpen);
  const { data: dimensionList } = useGetDimensionsList();
  const { column, enableAction } = useContext(DimensionContext);

  const sensors = useSensors(useSensor(PointerSensor));

  const dimensions = preset?.dimensions ?? [];

  const isRenamePreset =
    curPresetName === ERenamePreset.PAGE || curPresetName === ERenamePreset.SCREEN;
  let draggableDimensionNameList;
  if (isRenamePreset) {
    draggableDimensionNameList = dimensions.map((i) => i.dimensionName);
  } else {
    draggableDimensionNameList = dimensions
      .map((i) => i.dimensionName)
      .filter((i) => !exploreLockedDimensionNames.includes(i));
  }

  const handleTitleChange = useCallback(
    (newTitle: string, index: number) => {
      if (!preset) return;
      const newDimension = (dimensionList?.data ?? []).find(
        (dimension) => dimension.dimension_name === newTitle,
      );
      if (!newDimension) return;
      const resDimension = {
        dimensionName: newDimension.dimension_name,
        displayName: newDimension.display_name,
        dimensionType: newDimension.dimension_type,
      };
      const newDimensions = [...dimensions];
      const existingIndex = newDimensions
        .map((dimension) => dimension.dimensionName)
        .indexOf(newTitle);
      if (existingIndex !== -1) {
        const temp = { ...newDimensions[existingIndex] };
        newDimensions[existingIndex] = { ...newDimensions[index] };
        newDimensions[index] = temp;
      } else {
        newDimensions.splice(index, 1, resDimension);
      }
      const newPreset = { ...preset };
      newPreset.dimensions = newDimensions;
      setPreset({ ...newPreset });
      updatePreset(newPreset.id, { ...newPreset });
    },
    [dimensions, preset, dimensionList],
  );

  const handleClose = useCallback(
    (index: number) => {
      if (!preset) return;
      const newDimensions = [...dimensions];
      newDimensions.splice(index, 1);
      const newPreset = { ...preset };
      newPreset.dimensions = newDimensions;
      setPreset({ ...newPreset });
      updatePreset(newPreset.id, { ...newPreset });
    },
    [dimensions, preset],
  );

  const dimensionTables = useMemo(() => {
    return dimensions.map((dimension, index) => {
      let dimensionListData = dimensionList?.data ?? [];
      if (curPresetName === 'Explore' && eventDimensionNames.includes(dimension.dimensionName)) {
        dimensionListData = dimensionList?.data?.filter((d) =>
          eventDimensionNames.includes(d.dimension_name),
        );
      }

      const isEventTagKey =
        dimension.dimensionName === mappedEventDimensionType.merged_event_tag_key;

      return (
        <DimensionTableContainer
          data={dimension}
          size={size}
          column={column}
          enableAction={enableAction || (isEventTagKey && isNewWorkflowPage)} // Display tag key kebab in the EventLooker of new workflow.
          key={`${dimension.dimensionName}/${index}`}
          dimensionList={dimensionListData}
          onTitleChange={(title: string) => handleTitleChange(title, index)}
          onClose={() => handleClose(index)}
          lockedDimensionNames={lockedDimensionNames}
          eventAttributeDimensionNames={eventAttributeDimensionNames}
          transformPanelOpen={transformPanelOpen}
        />
      );
    });
  }, [
    column,
    dimensions,
    size,
    dimensionList,
    lockedDimensionNames,
    eventAttributeDimensionNames,
    transformPanelOpen,
    isNewWorkflowPage,
  ]);

  const handleDragEnd = (event) => {
    if (!preset) return;
    const { active, over } = event;
    const lockedNum = isRenamePreset ? 0 : 4;
    const sourceIdx = draggableDimensionNameList.indexOf(active.id) + lockedNum;
    const destIdx = draggableDimensionNameList.indexOf(over.id) + lockedNum;
    // if over on locked dimension do not drop.
    if (destIdx < lockedNum) return;
    // create new dimension list
    const newDimensionList = Array.from(dimensions);
    const [reorderedItem] = newDimensionList.splice(sourceIdx, 1);
    newDimensionList.splice(destIdx, 0, reorderedItem);

    const newPreset = { ...preset };
    newPreset.dimensions = newDimensionList;
    setPreset({ ...newPreset });
    updatePreset(newPreset.id, { ...newPreset });
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={draggableDimensionNameList} strategy={rectSortingStrategy}>
        <div
          className={styles.rootContainer}
          style={column === 3 ? { minWidth: 880 } : { minWidth: 1180 }} // HP-9028 Ensure that the headers of the dimension table are displayed completely.
        >
          {dimensionTables}
        </div>
      </SortableContext>
    </DndContext>
  );
};
